<template>
  <div class="container-fluid main-container-fluid" :key="orgId + '- ' + subOrgId + '-' + ownerId">
    <page-header
      :timeline="query.interval"
      :org-id="orgId"
      :team-id="$store.getters.rootTeamByOrgId(orgId).id"
      :show-team-members="false"
      :selected-interval="selectedInterval"
      :show-inactive-timeline-info="true"
      heading="Your Work"
      @update:selectedInterval="changeInterval($event)"
    >
      <template v-slot:title>
        <user-block :user-id="ownerId" />
      </template>
    </page-header>
    <div class="row justify-content-center pt-1 content-wrapper">
      <checkin-reminder-bar
        ref="checkinReminderBar"
        @checkin-posted="getUserGoals"
        :org-id="orgId"
        :goals="goals"
        :source="SOURCE.YOUR_WORK"
        v-if="goals"
      />
      <div class="col-12 col-md-10 col-lg-10 onboarding-banner-wrapper" v-if="showOnboardingBanner">
        <onboarding-banner @dismiss="dismissOnboardingBanner" :org-id="orgId" />
      </div>
      <div class="d-flex mw-0 w-100 user-contents-wrapper mb-5" v-else-if="allowNewLayout">
        <div class="user-items mw-0 flex-grow-1">
          <info-box v-if="isEmptySpace" class="my-5 mx-auto">
            <template slot="message">
              {{ isLoggedInUser ? "You don't" : `${user.firstName || user.email} doesn't` }} have
              any work assigned to
              {{ isLoggedInUser ? 'you' : 'them' }}
              in the selected timeline({{ selectedInterval.name }}).
              <template v-if="isLoggedInUser">
                When you do, this is where you will see them. Happy climbing!
              </template>
            </template>
            <add-goal-actions
              class="add-goal-actions"
              :expand-goal-on-creation-page="true"
              :org-id="orgId"
            />
          </info-box>
          <user-assigned-items
            :key="'goals-' + orgId + selectedInterval.id"
            :user-id="userId"
            :selected-timeline="selectedInterval"
            :org-id="orgId"
            :user-goals-fetcher="getUserGoals"
            :user-initiatives-fetcher="getUserInitiatives"
            @archived="getUserGoals"
            v-else
          />
        </div>
        <div class="feeds">
          <user-updates :org-id="orgId" :sub-org-id="subOrgId" />
        </div>
      </div>
      <div class="col-12 col-md-10 col-lg-10 content" ref="listContainer" v-else>
        <activity-card
          v-if="isLoggedInUser"
          :key="'activity-' + orgId + selectedInterval.id"
          :org-id="orgId"
          :sub-org-id="subOrgId"
        />
        <upcoming-initiatives
          :key="'upcoming-initiative-' + orgId + selectedInterval.id"
          :org-id="orgId"
          :user-id="userId"
          :selected-timeline="selectedInterval"
          :fetcher="upcomingInitiativesFetcher"
        />
        <upcoming-initiatives
          :key="'upcoming-tasks-' + orgId + selectedInterval.id"
          :org-id="orgId"
          :user-id="userId"
          :selected-timeline="selectedInterval"
          :fetcher="$event => upcomingInitiativesFetcher({ ...$event, hasParent: true })"
          :show-tasks-only="true"
          v-if="isTasksFeatureEnabled"
        />
        <user-assigned-items-card
          :key="'goals-' + orgId + selectedInterval.id"
          :user-id="userId"
          :selected-timeline="selectedInterval"
          :org-id="orgId"
          :user-goals-fetcher="getUserGoals"
          :user-initiatives-fetcher="getUserInitiatives"
          @archived="getUserGoals"
        />
        <card class="no-result-card flex-center" v-if="isEmptySpace">
          <info-box>
            <template slot="message">
              {{ isLoggedInUser ? "You don't" : `${user.firstName} doesn't` }} have any work
              assigned to
              {{ isLoggedInUser ? 'you' : 'them' }}
              in the selected timeline({{ selectedInterval.name }}).
              <template v-if="isLoggedInUser">
                When you do, this is where you will see them. Happy climbing!
              </template>
            </template>
            <add-goal-actions
              class="add-goal-actions"
              :expand-goal-on-creation-page="true"
              :org-id="orgId"
            />
          </info-box>
        </card>
        <hr />
        <quote />
        <tour
          @cancel="onTourCancel"
          @complete="onTourComplete"
          v-if="!isAppTourSeen"
          :steps="APP_TOUR_CONFIG"
          :meta="TOUR.APP"
        ></tour>
      </div>
    </div>
  </div>
</template>

<script>
import { trackAnalyticsEvent } from '@/analytics'
import ActivityCard from '@/components/ActivityCard'
import Card from '@/components/Card'
import CheckinReminderBar from '@/components/CheckinReminderBar'
import AddGoalActions from '@/components/goal/AddGoalActions.vue'
import InfoBox from '@/components/InfoBox'
import PageHeader from '@/components/PageHeader'
import OnboardingBanner from '@/components/tour/OnboardingBanner'
import { APP_TOUR_CONFIG } from '@/components/tour/tour-config/appTour'
import { TOUR } from '@/components/tour/tour-config/tours'
import Tour from '@/components/tour/Tour.vue'
import Quote from '@/components/user-page/Quote'
import UpcomingInitiatives from '@/components/user-page/UpcomingInitiatives'
import UserAssignedItems from '@/components/user-page/UserAssignedItems.vue'
import UserAssignedItemsCard from '@/components/user-page/UserAssignedItemsCard.vue'
import UserUpdates from '@/components/user-page/UserUpdates.vue'
import UserBlock from '@/components/UserBlock.vue'
import { EVENT_ACTION, EVENT_CATEGORY, SOURCE } from '@/constants/analytics'
import { featureFlags } from '@/constants/featureFlags'
import { INITIATIVE_STANDARD_FIELD_NAME } from '@/constants/initiative'
import { STORAGE_FLAGS } from '@/constants/storage'
import { storageFlags } from '@/utils/storageUtils'
import observe from '@/directives/observe'
import EventBus from '@/EventBus'
import { defaultInterval, intervalWithId, nowInterval, setDefaultIntervalId } from '@/interval'
import DragAndDropMixin from '@/mixins/DragAndDropMixin'
import FeaturesMixin from '@/mixins/FeaturesMixin'
import MediaQueryMixin from '@/mixins/MediaQueryMixin'
import MembershipsMixin from '@/mixins/MembershipsMixin'
import * as keymapService from '@/services/keymapService/keymapService'
import addDaysToDate from '@/utils/addDaysToDate'
import { convertToDateString } from '@/utils/formatDate'
import getDocumentTitle from '@/utils/getDocumentTitle'
import getVisitedTours from '@/utils/getVisitedTours'
import * as R from 'ramda'
import { mapActions } from 'vuex'
import GoalsMixin from './GoalsMixin'

export default {
  name: 'YourWork',
  mixins: [GoalsMixin, MembershipsMixin, DragAndDropMixin, MediaQueryMixin, FeaturesMixin],
  components: {
    Tour,
    AddGoalActions,
    UserUpdates,
    UserAssignedItems,
    PageHeader,
    CheckinReminderBar,
    OnboardingBanner,
    InfoBox,
    ActivityCard,
    UserAssignedItemsCard,
    UpcomingInitiatives,
    Card,
    Quote,
    UserBlock
  },
  directives: {
    observe
  },
  props: ['orgId', 'query', 'userId', 'subOrgId'],
  data() {
    return {
      userInitiatives: null,
      upcomingTasks: null,
      upcomingInitiatives: null,
      goals: null,
      isAppTourSeen: getVisitedTours().includes(TOUR.APP.name),
      SOURCE,
      APP_TOUR_CONFIG,
      TOUR: TOUR,
      onboardingBannerDismissed: storageFlags.get(STORAGE_FLAGS.onboardingBannerDismissed),
      isTasksFeatureEnabled: this.getFeatureFlagValue(this.orgId, featureFlags.ALLOW_TASKS)
    }
  },
  computed: {
    allowNewLayout() {
      return this.getFeatureFlagValue(this.orgId, featureFlags.ALLOW_YOUR_WORK_NEW_LAYOUT)
    },
    org() {
      return this.$store.getters.orgById(this.orgId)
    },
    ownerId() {
      const userId = this.userId || this.$store.getters.user.id
      return Number(userId)
    },
    isLoggedInUser() {
      const loggedInUser = this.$store.getters.user
      return this.ownerId === loggedInUser.id
    },
    selectedInterval() {
      const interval = this.query.interval
      if (interval) {
        return intervalWithId(interval)
      } else {
        return defaultInterval()
      }
    },
    user() {
      return this.$store.getters.userById(this.ownerId) || {}
    },
    isEmptySpace() {
      const oldLayoutItems = [this.goals, this.userInitiatives, this.upcomingInitiatives]
      if (this.isTasksFeatureEnabled) {
        oldLayoutItems.push(this.upcomingTasks)
      }
      const items = this.allowNewLayout ? [this.goals, this.userInitiatives] : oldLayoutItems
      return items.every(items => items?.length === 0)
    },
    showOnboardingBanner() {
      return (
        this.isEmptySpace &&
        !this.onboardingBannerDismissed &&
        this.isLoggedInUser &&
        this.selectedInterval.id === nowInterval().id &&
        !this.isMobileMode
      )
    }
  },
  methods: {
    ...mapActions(['getGoals', 'getInitiatives']),
    async getUserGoals() {
      const goals = await this.getGoals({
        orgId: this.orgId,
        type: null,
        teamId: null,
        options: {
          ownerId: this.ownerId,
          intervalStart: this.selectedInterval.start,
          intervalEnd: this.selectedInterval.end,
          intervalSubset: this.selectedInterval.subset,
          isArchived: false,
          scopeId: this.subOrgId,
          expand: 'parent_goal_ids'
        }
      })
      this.goals = goals
      return goals
    },
    async refreshStuff() {
      setDefaultIntervalId(this.selectedInterval.id)
      this.goals = null
      this.userInitiatives = null
      this.upcomingInitiatives = null
      this.upcomingTasks = null
    },
    async getUserInitiatives(additionalParams) {
      const params = {
        orderBy: INITIATIVE_STANDARD_FIELD_NAME.DUE_DATE,
        ownerIds: this.ownerId,
        intervalStart: this.selectedInterval.start,
        intervalEnd: this.selectedInterval.end,
        intervalSubset: this.selectedInterval.subset,
        isClosed: false,
        isArchived: false,
        includeCustomFields: true,
        scopeId: this.subOrgId,
        orgId: this.orgId,
        hasParent: false,
        ...additionalParams
      }
      const initiatives = await this.getInitiatives(params)
      this.userInitiatives = initiatives.results
      return initiatives
    },
    changeInterval(interval) {
      this.$router.push({
        name: this.$route.name,
        params: { orgId: this.orgId, subOrgId: this.subOrgId },
        query: {
          interval: interval.id
        }
      })
    },
    dismissOnboardingBanner() {
      storageFlags.set(STORAGE_FLAGS.onboardingBannerDismissed)
      this.onboardingBannerDismissed = true
    },
    getDateAfterDays(days) {
      const date = addDaysToDate(new Date(), days)
      return convertToDateString(date)
    },
    async upcomingInitiativesFetcher(additionalParams = {}) {
      const dueDate = this.getDateAfterDays(15)
      const params = {
        orderBy: INITIATIVE_STANDARD_FIELD_NAME.DUE_DATE,
        ownerIds: this.ownerId,
        intervalStart: this.selectedInterval.start,
        intervalEnd: this.selectedInterval.end,
        intervalSubset: this.selectedInterval.subset,
        isClosed: false,
        filterOp: [`${INITIATIVE_STANDARD_FIELD_NAME.DUE_DATE}~lte~["${dueDate}"]`],
        isArchived: false,
        includeCustomFields: true,
        scopeId: this.subOrgId,
        orgId: this.orgId,
        hasParent: false,
        ...additionalParams
      }
      const initiatives = await this.getInitiatives(params)
      if (additionalParams.hasParent) {
        this.upcomingTasks = initiatives.results
      } else {
        this.upcomingInitiatives = initiatives.results
      }
      return initiatives
    },
    showBulkCheckinModal() {
      this.$refs.checkinReminderBar?.showCheckInModal()
    },
    initializeKeyMapService() {
      keymapService.activateCategory('yourWork', {
        'cancel-editing': this.cancelEditing,
        'bulk-checkin': this.showBulkCheckinModal
      })
    },
    stopKeyMapService() {
      keymapService.deactivateCategory('yourWork')
    },
    updateDocumentTitle() {
      if (!this.isLoggedInUser && !R.isEmpty(this.user)) {
        const team = this.$store.getters.teamById(this.subOrgId)
        document.title = getDocumentTitle({
          teamName: team.name,
          title: this.user.displayName
        })
      }
    },
    onTourCancel() {
      trackAnalyticsEvent(EVENT_CATEGORY.NAVIGATION, EVENT_ACTION.IN_APP_TOUR_SKIPPED)
      this.isAppTourSeen = true
    },
    onTourComplete() {
      trackAnalyticsEvent(EVENT_CATEGORY.NAVIGATION, EVENT_ACTION.IN_APP_TOUR_COMPLETED)
      this.isAppTourSeen = true
    },
    triggerTourEvent() {
      if (!this.isAppTourSeen) {
        // tour started
        trackAnalyticsEvent(EVENT_CATEGORY.NAVIGATION, EVENT_ACTION.IN_APP_TOUR_STARTED)
      }
    }
  },
  async mounted() {
    this.updateOrgMemberships(this.orgId).then(members => {
      const member = members.find(R.propEq('userId', this.ownerId))
      if (!member) {
        EventBus.$emit('SHOW_404')
      }
    })
    await this.refreshStuff()
    this.initializeKeyMapService()
    setTimeout(() => {
      this.updateDocumentTitle()
    }, 0)
    this.triggerTourEvent()
  },
  beforeDestroy() {
    this.stopKeyMapService()
  },
  watch: {
    orgId: 'refreshStuff',
    subOrgId: 'refreshStuff',
    selectedInterval: 'refreshStuff',
    user: 'updateDocumentTitle',
    ownerId() {
      this.userInitiatives = null
      this.upcomingInitiatives = null
      this.upcomingTasks = null
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/styleConfigurations';

$initiative-section-min-height: 30px;

.content {
  display: flex;
  flex-direction: column;
  gap: 25px;
  padding: 16px;
  background: linear-gradient(
    180deg,
    #fafafa 0%,
    rgba(250, 250, 250, 0.84375) 46.88%,
    rgba(250, 250, 250, 0) 100%
  );
  border-radius: 5px;
  margin-bottom: 100px;
}

.content-wrapper {
  padding: 0 12px;
}

/deep/ .goal-skeleton {
  padding-left: 27px;
  border-top: 1px solid $borderSecondary;
}

.onboarding-banner-wrapper {
  display: flex;
  justify-content: center;
  align-items: baseline;
  margin-top: 40px;
}

.sunrise-img {
  @include dimension(86px, 86px);
}

@include media-breakpoint-down(md) {
  /deep/ .initiative-row .caret {
    display: none;
  }
  /deep/ .goal-skeleton {
    padding-left: 0;
  }
  .content-wrapper {
    padding: 0;
  }
  .content {
    padding: 16px 8px;
  }
}

.no-result-card {
  height: 500px;
}

hr {
  width: 80px;
  margin: 0 auto;
}

.feeds {
  width: 410px;
  flex-shrink: 0;
}

.user-contents-wrapper {
  gap: 16px;
}

/deep/ .desktop-header-container {
  // FIXME: achieve this via component props, without css overriding
  padding: 0 24px;
  flex-basis: 100% !important;
  max-width: 100% !important;
}

.add-goal-actions {
  z-index: 1;
}

@include media-breakpoint-down(lg) {
  .user-contents-wrapper {
    flex-direction: column;
  }
  .feeds {
    width: auto;
  }
}

@include media-breakpoint-down(md) {
  .content-wrapper {
    padding: 0 !important;
  }
}
</style>
